var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { playAudio } from '../helper/audioHelper';
import { saveState } from '../helper/stateManagerHelper';
import { announceAssertive } from '../helper/announcer';
import { getFractionDimensions, sumAndReduceFractions, parseFraction, gcd } from '../helper/helper';
import { getDenominatorFromFractionType } from './SplitFractionTileModal';
import { getSnappedPosition } from '../helper/snapHelper';
import { snapTileToNumberLine } from '../helper/numberLineHelper';
import { FRACTION_CONFIG } from '../constants/fractionConfig';
import globalStore from '../store';
var FractionTileController = function () {
    var _a = useContext(DataContext), manipulative = _a.manipulative, setManipulative = _a.setManipulative, selectedTool = _a.selectedTool, setSelectedTool = _a.setSelectedTool, selectedManipulativeId = _a.selectedManipulativeId, setSelectedManipulativeId = _a.setSelectedManipulativeId, setShowSplitFractionTileModal = _a.setShowSplitFractionTileModal, setSplitFractionTileCurrentDenominator = _a.setSplitFractionTileCurrentDenominator, setSplitFractionTileCurrentNumerator = _a.setSplitFractionTileCurrentNumerator, savedState = _a.savedState;
    var _b = globalStore.useStoreState(function (state) { return state.playerStore; }), zoomLevel = _b.zoomLevel, playerBgPattern = _b.playerBgPattern;
    useEffect(function () {
        var handleEvents = function (data) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
            var newStateToSave = [];
            switch (data.id) {
                case 'createFractionTile':
                    if (data.data) {
                        var _12 = data.data, fractionType = _12.fractionType, dropX = _12.dropX, dropY = _12.dropY, eventShapeType = _12.shapeType, eventLablesType = _12.lablesType, eventColor = _12.color;
                        // Convert screen coordinates to canvas coordinates.
                        // Use .mainHolder (the CSS-scaled canvas element) as the origin so the
                        // margin: auto centering of the canvas at zoom < 100% is accounted for.
                        // Fallback to the scroll container only if mainHolder isn't found.
                        var scrollEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
                        var mainBoard = document.querySelector('.mainHolder');
                        var canvasEl = mainBoard || scrollEl;
                        if (canvasEl) {
                            var rect = canvasEl.getBoundingClientRect();
                            // Get the currently selected fraction tile to match its properties
                            var selectedFractionTile = (_a = manipulative.find(function (m) { return m.data.type === 'fractionTiles' && m.data.id === selectedManipulativeId; })) === null || _a === void 0 ? void 0 : _a.data;
                            var shapeType_1 = eventShapeType || (selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.shapeType) || 'rectangle';
                            var lablesType = eventLablesType || (selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.lablesType) || 'labeled';
                            var color = (_b = eventColor !== null && eventColor !== void 0 ? eventColor : selectedFractionTile === null || selectedFractionTile === void 0 ? void 0 : selectedFractionTile.color) !== null && _b !== void 0 ? _b : '';
                            var config = getFractionDimensions(fractionType);
                            var rectDims = config ? config.rectangle : { width: 120, height: 55 };
                            // Match the scale already in use for this shape type on the whiteboard
                            var existingTileOfSameShape = (_c = manipulative.find(function (m) {
                                return m.data.type === 'fractionTiles' &&
                                    m.data.shapeType === shapeType_1;
                            })) === null || _c === void 0 ? void 0 : _c.data;
                            var tileScale = (_d = existingTileOfSameShape === null || existingTileOfSameShape === void 0 ? void 0 : existingTileOfSameShape.scale) !== null && _d !== void 0 ? _d : 1;
                            var baseWidth = shapeType_1 === 'circle' ? 300 : rectDims.width;
                            var baseHeight = shapeType_1 === 'circle' ? 300 : rectDims.height;
                            var dimensions_1 = {
                                width: baseWidth * tileScale,
                                height: baseHeight * tileScale,
                            };
                            var x = (dropX - rect.left) / zoomLevel;
                            var y = (dropY - rect.top) / zoomLevel;
                            var canvasX = Math.round(x - dimensions_1.width / 2);
                            var canvasY = Math.round(y - dimensions_1.height / 2);
                            // Create new fraction tile
                            var newTileId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            var snapResult = getSnappedPosition({ x: canvasX, y: canvasY }, dimensions_1, shapeType_1, manipulative, newTileId, undefined, { fractionSize: fractionType });
                            // --- GRID SNAPPING LOGIC FOR PLACEMENT ---
                            var finalX = snapResult.x;
                            var finalY = snapResult.y;
                            var finalScale = tileScale;
                            var finalDimensions = dimensions_1;
                            var nlSnapNumberLineId = void 0;
                            // --- NUMBER LINE SNAPPING LOGIC (dropped from the tool panel) ---
                            // Mirrors the same check in FractionTileManipulative.tsx's drag-drop
                            // handler — without this, a tile created here (rather than dragged
                            // on-canvas) never snapped to a nearby Number Line.
                            if (shapeType_1 === 'rectangle' && !snapResult.isSnappedX && !snapResult.isSnappedY) {
                                var nlSnap = snapTileToNumberLine({ x: canvasX, y: canvasY }, { width: baseWidth, height: baseHeight }, tileScale, manipulative, newTileId);
                                if (nlSnap) {
                                    finalX = nlSnap.x;
                                    finalY = nlSnap.y;
                                    finalScale = nlSnap.scale;
                                    finalDimensions = {
                                        width: baseWidth * nlSnap.scale,
                                        height: baseHeight * nlSnap.scale,
                                    };
                                    nlSnapNumberLineId = nlSnap.numberLineId;
                                }
                            }
                            // --- END NUMBER LINE SNAPPING LOGIC ---
                            var GRID_SIZE = 55;
                            if (playerBgPattern === 'square' &&
                                shapeType_1 === 'rectangle' &&
                                !snapResult.isSnappedX &&
                                !snapResult.isSnappedY &&
                                !nlSnapNumberLineId) {
                                finalX = Math.round(canvasX / GRID_SIZE) * GRID_SIZE;
                                finalY = Math.round(canvasY / GRID_SIZE) * GRID_SIZE;
                            }
                            // Play snap sound when tile snaps to another tile on drop from panel
                            if (snapResult.isSnappedX || snapResult.isSnappedY) {
                                playAudio('PatternAdd');
                            }
                            // ----------------------------------------
                            // --- SNAPPED ID ASSIGNMENT FOR RIGHT-PANEL DROP ---
                            // Assign snappedId so scale repositioning can chain this tile with its neighbours.
                            // Only applies to rectangles (circles use center-sharing, not snappedId).
                            var newTileSnappedId_1;
                            if (shapeType_1 === 'rectangle') {
                                var modIds = new Set(((_e = snapResult.modifications) !== null && _e !== void 0 ? _e : []).map(function (m) { return m.id; }));
                                var finalPos_1 = { x: finalX, y: finalY };
                                // 1. Inherit from mod tiles (row-insertion neighbours).
                                for (var _i = 0, manipulative_1 = manipulative; _i < manipulative_1.length; _i++) {
                                    var m = manipulative_1[_i];
                                    if (m.data.type !== 'fractionTiles')
                                        continue;
                                    var d = m.data;
                                    if (!modIds.has(d.id))
                                        continue;
                                    if (d.snappedId) {
                                        newTileSnappedId_1 = d.snappedId;
                                        break;
                                    }
                                }
                                // 2. Inherit from adjacent tiles (horizontal or vertical neighbours).
                                if (!newTileSnappedId_1) {
                                    var isAdjacentToNew = function (d) {
                                        if (d.shapeType !== 'rectangle')
                                            return false;
                                        var sameRow = Math.abs(d.center.y - finalPos_1.y) < 10;
                                        var sameCol = d.center.x < finalPos_1.x + dimensions_1.width + 5 &&
                                            d.center.x + d.dimensions.width > finalPos_1.x - 5;
                                        if (sameRow) {
                                            var myRight = d.center.x + d.dimensions.width;
                                            return (Math.abs(finalPos_1.x - myRight) < 5 ||
                                                Math.abs(finalPos_1.x + dimensions_1.width - d.center.x) < 5);
                                        }
                                        if (sameCol) {
                                            var dBottom = d.center.y + d.dimensions.height;
                                            // Use tight tolerance (5px) so only nearly-touching vertical placements
                                            // inherit the cluster — prevents separate groups from merging.
                                            return (Math.abs(finalPos_1.y - dBottom) < 5 ||
                                                Math.abs(finalPos_1.y + dimensions_1.height - d.center.y) < 5);
                                        }
                                        return false;
                                    };
                                    for (var _13 = 0, manipulative_2 = manipulative; _13 < manipulative_2.length; _13++) {
                                        var m = manipulative_2[_13];
                                        if (m.data.type !== 'fractionTiles')
                                            continue;
                                        var d = m.data;
                                        if (modIds.has(d.id) || d.id === newTileId)
                                            continue;
                                        if (isAdjacentToNew(d) && d.snappedId) {
                                            newTileSnappedId_1 = d.snappedId;
                                            break;
                                        }
                                    }
                                }
                                // 3. No existing cluster — create a new one.
                                if (!newTileSnappedId_1)
                                    newTileSnappedId_1 = uuidv4();
                            }
                            // -----------------------------------------------
                            var newTile_1 = {
                                type: 'fractionTiles',
                                data: {
                                    id: newTileId,
                                    type: 'fractionTiles',
                                    center: { x: finalX, y: finalY },
                                    dimensions: finalDimensions,
                                    defaultDimensions: { width: baseWidth, height: baseHeight },
                                    scale: finalScale,
                                    lock: {
                                        positioning: false,
                                        editing: false,
                                    },
                                    fractionSize: fractionType,
                                    shapeType: shapeType_1,
                                    lablesType: lablesType,
                                    customLabel: '',
                                    color: color,
                                    rotation: (_f = snapResult.rotation) !== null && _f !== void 0 ? _f : 0,
                                    snappedId: newTileSnappedId_1,
                                    numberLineId: nlSnapNumberLineId,
                                },
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            var stateChanges_1 = [];
                            stateChanges_1.push({
                                type: newTileId,
                                id: newTileId.split('_')[1],
                                propertyName: 'create',
                                old: JSON.stringify(newTile_1),
                            });
                            var modifiedTiles_1 = snapResult.modifications || [];
                            // Compute undo entries using the snapshot at event time (acceptable for history).
                            modifiedTiles_1.forEach(function (mod) {
                                var _a;
                                var m = manipulative.find(function (item) { return item.data.id === mod.id; });
                                if (!m)
                                    return;
                                var d = m.data;
                                stateChanges_1.push({
                                    type: m.data.id || '',
                                    id: (m.data.id || '').split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(m.data.center),
                                    new: JSON.stringify(mod.center),
                                });
                                if (newTileSnappedId_1 && d.snappedId !== newTileSnappedId_1) {
                                    stateChanges_1.push({
                                        type: m.data.id || '',
                                        id: (m.data.id || '').split('_')[1],
                                        propertyName: 'snappedId',
                                        old: JSON.stringify((_a = d.snappedId) !== null && _a !== void 0 ? _a : null),
                                        new: JSON.stringify(newTileSnappedId_1),
                                    });
                                }
                            });
                            // Apply all state changes
                            newStateToSave.push.apply(newStateToSave, stateChanges_1);
                            // Functional update so tiles added by concurrent handlers are preserved in prev.
                            setManipulative(function (prev) {
                                var updated = prev.map(function (m) {
                                    var mod = modifiedTiles_1.find(function (modItem) { return modItem.id === m.data.id; });
                                    if (mod) {
                                        var d = m.data;
                                        return __assign(__assign({}, m), { data: __assign(__assign(__assign({}, m.data), { center: mod.center }), (newTileSnappedId_1 && d.snappedId !== newTileSnappedId_1
                                                ? { snappedId: newTileSnappedId_1 }
                                                : {})) });
                                    }
                                    return m;
                                });
                                return __spreadArray(__spreadArray([], updated, true), [newTile_1], false);
                            });
                            setSelectedTool([newTileId]);
                            setSelectedManipulativeId(newTileId);
                        }
                    }
                    break;
                case 'glueFractionTiles': {
                    var explicitIds = (_g = data.data) === null || _g === void 0 ? void 0 : _g.tileIds;
                    var stripLabel_1 = function (t) { return t.replace(/_custom_label_(rect|circle)_\d+$/, ''); };
                    // Resolve any manipulativeGroup_ token to its member fraction-tile ids —
                    // otherwise clicking Combine while a group is selected silently no-ops,
                    // since no manipulative item's id equals the groupId itself.
                    var idsToUse = (explicitIds !== null && explicitIds !== void 0 ? explicitIds : selectedTool).flatMap(function (sid) {
                        if (sid.startsWith('manipulativeGroup_')) {
                            return manipulative
                                .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === sid; })
                                .map(function (m) { return m.data.id; });
                        }
                        return [stripLabel_1(sid)];
                    });
                    var selectedTiles = idsToUse
                        .map(function (sid) { return manipulative.find(function (m) { return m.data.id === sid; }); })
                        .filter(function (m) { return m && m.data.type === 'fractionTiles'; });
                    var circleTiles = selectedTiles.filter(function (t) { return t.data.shapeType === 'circle'; });
                    var rectTiles = selectedTiles.filter(function (t) { return t.data.shapeType === 'rectangle'; });
                    var newGluedTiles_1 = [];
                    var removedTileIds_1 = [];
                    var stateChanges = [];
                    // Expand a selected tile into its flat list of true leaf pieces. If the
                    // tile is already a compound (non-empty gluedFrom), recurse into its
                    // leaves instead of nesting the whole compound as one gluedFrom entry —
                    // gluedFrom must always stay flat (every consumer in the codebase reads
                    // it as a flat array of leaves). Without this, selecting 2+ already-glued
                    // tiles and combining them again produced a gluedFrom entry that itself
                    // had a non-empty gluedFrom, which the unglue logic (one level deep only)
                    // couldn't fully unwind — leaving stale/mismatched undo-redo history.
                    var flattenToLeaves_1 = function (t) {
                        var td = t.data;
                        if (td.gluedFrom && td.gluedFrom.length > 0) {
                            return td.gluedFrom.flatMap(function (leaf) {
                                return leaf.gluedFrom && leaf.gluedFrom.length > 0
                                    ? flattenToLeaves_1({ type: 'fractionTiles', data: leaf })
                                    : [JSON.parse(JSON.stringify(leaf))];
                            });
                        }
                        return [JSON.parse(JSON.stringify(td))];
                    };
                    // Helper to get absolute start angle for circles.
                    // Rounded to the nearest integer degree — standard fraction wedges always
                    // land on exact integer degrees (e.g. 360/12=30), and snapHelper's adjacency
                    // checks use a 1deg-resolution grid. Without rounding, floating-point error
                    // from repeated glue operations slowly drifts the stored rotation off that
                    // grid until adjacency checks start silently failing after several
                    // glue/split cycles (most visible with small wedges like twelfths).
                    var getAbsoluteStart_1 = function (t) {
                        var _a, _b, _c;
                        var td = t.data;
                        if (td.customSweep != null)
                            return Math.round(((((_a = td.rotation) !== null && _a !== void 0 ? _a : 0) % 360) + 360) % 360);
                        var config = ((_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) || {
                            start: 0,
                            sweep: 360,
                        };
                        return Math.round((((config.start + ((_c = td.rotation) !== null && _c !== void 0 ? _c : 0)) % 360) + 360) % 360);
                    };
                    // 1. Glue all selected circle tiles into one (if 2+)
                    if (circleTiles.length >= 2) {
                        // Sort by absolute visual start angle for natural clockwise arrangement
                        circleTiles.sort(function (a, b) { return getAbsoluteStart_1(a) - getAbsoluteStart_1(b); });
                        // A selection that skips some wedges can wrap across the 0deg/360deg
                        // boundary (e.g. the un-selected wedges sit right before 0 deg). Sorting
                        // by raw angle then always starts the arc at the smallest angle, which
                        // is wrong when that angle isn't actually where the selected run
                        // begins — it silently reconstructs the wrong arc, moving the combined
                        // sector to a different part of the circle. Find the largest circular
                        // gap between consecutive selected wedges (the removed chunk) and
                        // start the run right after it instead.
                        var getSweep = function (t) {
                            var _a, _b, _c, _d;
                            var td = t.data;
                            return (_d = (_a = td.customSweep) !== null && _a !== void 0 ? _a : (_c = (_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0;
                        };
                        var gapStartIndex = 0;
                        var largestGap = -Infinity;
                        for (var i = 0; i < circleTiles.length; i++) {
                            var cur = circleTiles[i];
                            var next = circleTiles[(i + 1) % circleTiles.length];
                            var curEnd = getAbsoluteStart_1(cur) + getSweep(cur);
                            var nextStart = getAbsoluteStart_1(next) + (i === circleTiles.length - 1 ? 360 : 0);
                            var gap = nextStart - curEnd;
                            if (gap > largestGap) {
                                largestGap = gap;
                                gapStartIndex = (i + 1) % circleTiles.length;
                            }
                        }
                        if (largestGap > 0.01 && gapStartIndex > 0) {
                            circleTiles = __spreadArray(__spreadArray([], circleTiles.slice(gapStartIndex), true), circleTiles.slice(0, gapStartIndex), true);
                        }
                        var totalSweep_1 = 0;
                        circleTiles.forEach(function (t) {
                            var _a, _b, _c, _d;
                            var td = t.data;
                            totalSweep_1 +=
                                (_d = (_a = td.customSweep) !== null && _a !== void 0 ? _a : (_c = (_b = getFractionDimensions(td.fractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 0;
                        });
                        if (totalSweep_1 > 360)
                            totalSweep_1 = 360;
                        var fractionStrings = circleTiles.map(function (t) {
                            var td = t.data;
                            if (td.gluedFrom && td.gluedFrom.length > 0) {
                                return sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }));
                            }
                            return td.fractionSize;
                        });
                        var newFractionSize = sumAndReduceFractions(fractionStrings);
                        var parsedFraction = parseFraction(newFractionSize) || {
                            numerator: 1,
                            denominator: 1,
                        };
                        var combinedNumerator = parsedFraction.numerator;
                        var combinedDenominator = parsedFraction.denominator === 0 ? 1 : parsedFraction.denominator;
                        var firstTile_1 = circleTiles[0].data;
                        var anchorAbsoluteStart = getAbsoluteStart_1(circleTiles[0]);
                        var newGluedId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                        // Re-arrange for metadata. Flatten each selected tile into its true leaf
                        // pieces first — a selected tile may itself already be a compound from a
                        // prior glue, and gluedFrom must stay a flat array of leaves (see
                        // flattenToLeaves above).
                        var currentAbsoluteStart_1 = anchorAbsoluteStart;
                        var rearrangedGluedFrom = circleTiles.flatMap(function (t) {
                            return flattenToLeaves_1(t).map(function (leaf) {
                                var _a, _b;
                                var config = ((_a = getFractionDimensions(leaf.fractionSize)) === null || _a === void 0 ? void 0 : _a.circle) || {
                                    start: 0,
                                    sweep: 360,
                                };
                                var sweep = (_b = leaf.customSweep) !== null && _b !== void 0 ? _b : config.sweep;
                                var metaTile = __assign(__assign({}, leaf), { center: __assign({}, firstTile_1.center), 
                                    // Calculate rotation such that (rotation + config.start) == currentAbsoluteStart.
                                    // Rounded to whole degrees — see getAbsoluteStart's rounding note above; without
                                    // this the stepped sum below compounds float error across each wedge in the run.
                                    rotation: Math.round((currentAbsoluteStart_1 - (leaf.customSweep != null ? 0 : config.start) + 360) %
                                        360) });
                                currentAbsoluteStart_1 = Math.round((currentAbsoluteStart_1 + sweep) % 360);
                                return metaTile;
                            });
                        });
                        var gluedTile = {
                            type: 'fractionTiles',
                            data: __assign(__assign({}, JSON.parse(JSON.stringify(firstTile_1))), { id: newGluedId, type: 'fractionTiles', center: __assign({}, firstTile_1.center), dimensions: { width: 300, height: 300 }, defaultDimensions: { width: 300, height: 300 }, scale: (_h = firstTile_1.scale) !== null && _h !== void 0 ? _h : 1, lock: { positioning: false, editing: false }, 
                                // Combining always yields a standalone tile — never inherit the
                                // source tile's group membership.
                                groupId: undefined, groupPosition: undefined, groupCenter: undefined, beforeGroupScale: undefined, groupScale: undefined, fractionSize: newFractionSize, shapeType: 'circle', lablesType: firstTile_1.lablesType, customLabel: '', color: firstTile_1.color, customSweep: parsedFraction.denominator === 0 ? 360 * parsedFraction.numerator : totalSweep_1, customNumerator: combinedNumerator, customDenominator: combinedDenominator, rotation: parsedFraction.denominator === 0 ? 0 : anchorAbsoluteStart, gluedFrom: rearrangedGluedFrom }),
                            widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                        };
                        stateChanges.push({
                            type: 'glueFractionTiles',
                            id: newGluedId.split('_')[1],
                            propertyName: 'glue',
                            old: JSON.stringify({
                                removedTiles: circleTiles.map(function (t) { return JSON.parse(JSON.stringify(t)); }),
                                createdTile: gluedTile,
                            }),
                        });
                        newGluedTiles_1.push(gluedTile);
                        removedTileIds_1.push.apply(removedTileIds_1, circleTiles.map(function (t) { return t.data.id; }));
                    }
                    // 2. Glue all selected rectangle tiles into one (if 2+)
                    if (rectTiles.length >= 2) {
                        // Determine orientation: vertical column vs horizontal row
                        var minX = Math.min.apply(Math, rectTiles.map(function (t) { return t.data.center.x; }));
                        var maxX = Math.max.apply(Math, rectTiles.map(function (t) {
                            return t.data.center.x +
                                t.data.dimensions.width;
                        }));
                        var minY = Math.min.apply(Math, rectTiles.map(function (t) { return t.data.center.y; }));
                        var maxY = Math.max.apply(Math, rectTiles.map(function (t) {
                            return t.data.center.y +
                                t.data.dimensions.height;
                        }));
                        var xRange = maxX - minX;
                        var yRange = maxY - minY;
                        // Heuristic for vertical column: spread in X is small relative to spread in Y
                        // and spread in X is less than a typical tile width (e.g., 55 or even less if snapped)
                        var isVerticalGlue_1 = xRange < yRange && xRange < 20;
                        if (isVerticalGlue_1) {
                            // Sort by Y position for top-to-bottom arrangement
                            rectTiles.sort(function (a, b) {
                                return a.data.center.y - b.data.center.y;
                            });
                        }
                        else {
                            // Sort by X position for natural left-to-right arrangement
                            rectTiles.sort(function (a, b) {
                                return a.data.center.x - b.data.center.x;
                            });
                        }
                        var totalWidth_1 = isVerticalGlue_1
                            ? rectTiles[0].data.dimensions.width
                            : 0;
                        var totalHeight_1 = isVerticalGlue_1
                            ? 0
                            : rectTiles[0].data.dimensions.height;
                        rectTiles.forEach(function (t) {
                            var td = t.data;
                            if (isVerticalGlue_1) {
                                totalHeight_1 += td.dimensions.height;
                            }
                            else {
                                totalWidth_1 += td.dimensions.width;
                            }
                        });
                        var fractionStrings = rectTiles.map(function (t) {
                            var td = t.data;
                            if (td.gluedFrom && td.gluedFrom.length > 0) {
                                return sumAndReduceFractions(td.gluedFrom.map(function (f) { return f.fractionSize; }));
                            }
                            return td.fractionSize;
                        });
                        var newFractionSize = sumAndReduceFractions(fractionStrings);
                        var parsedFraction = parseFraction(newFractionSize) || {
                            numerator: 1,
                            denominator: 1,
                        };
                        var finalNumerator = parsedFraction.numerator;
                        var finalDenominator = parsedFraction.denominator === 0 ? 1 : parsedFraction.denominator;
                        var firstRect = rectTiles[0].data;
                        var newGluedId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                        // Re-arrange for metadata. Flatten each selected tile into its true leaf
                        // pieces first — see flattenToLeaves above.
                        var currentX_1 = firstRect.center.x;
                        var currentY_1 = firstRect.center.y;
                        var rearrangedGluedFrom = rectTiles.flatMap(function (t) {
                            return flattenToLeaves_1(t).map(function (leaf) {
                                var metaTile = __assign(__assign({}, leaf), { center: { x: currentX_1, y: currentY_1 } });
                                if (isVerticalGlue_1) {
                                    currentY_1 += leaf.dimensions.height;
                                }
                                else {
                                    currentX_1 += leaf.dimensions.width;
                                }
                                return metaTile;
                            });
                        });
                        var gluedTile = {
                            type: 'fractionTiles',
                            data: __assign(__assign({}, JSON.parse(JSON.stringify(firstRect))), { id: newGluedId, 
                                // Combining always yields a standalone tile — never inherit the
                                // source tile's group membership.
                                groupId: undefined, groupPosition: undefined, groupCenter: undefined, beforeGroupScale: undefined, groupScale: undefined, fractionSize: newFractionSize, dimensions: { width: totalWidth_1, height: totalHeight_1 }, defaultDimensions: {
                                    width: totalWidth_1 / ((_j = firstRect.scale) !== null && _j !== void 0 ? _j : 1),
                                    height: totalHeight_1 / ((_k = firstRect.scale) !== null && _k !== void 0 ? _k : 1),
                                }, customLabel: '', customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: rearrangedGluedFrom }),
                            widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                        };
                        stateChanges.push({
                            type: 'glueFractionTiles',
                            id: newGluedId.split('_')[1],
                            propertyName: 'glue',
                            old: JSON.stringify({
                                removedTiles: rectTiles.map(function (t) { return JSON.parse(JSON.stringify(t)); }),
                                createdTile: gluedTile,
                            }),
                        });
                        newGluedTiles_1.push(gluedTile);
                        removedTileIds_1.push.apply(removedTileIds_1, rectTiles.map(function (t) { return t.data.id; }));
                    }
                    if (newGluedTiles_1.length > 0) {
                        saveState(stateChanges);
                        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !m.data.id || !removedTileIds_1.includes(m.data.id); }), true), newGluedTiles_1, true); });
                        var newIds = newGluedTiles_1.map(function (t) { return t.data.id; });
                        setSelectedTool(newIds);
                        setSelectedManipulativeId(newIds[0]);
                        playAudio('fractionTileTape');
                        // Delay so focus transitions settle before the assertive live region fires;
                        // otherwise VoiceOver / JAWS cancels the announcement with the focus speech.
                        setTimeout(function () { return announceAssertive('Tiles combined'); }, 200);
                    }
                    else {
                        // Attempt Unglue logic remains same if applicable
                        var gluedTiles = selectedTiles.filter(function (t) {
                            var td = t.data;
                            return td.gluedFrom && td.gluedFrom.length > 0;
                        });
                        if (gluedTiles.length > 0 && selectedTiles.length === gluedTiles.length) {
                            var allRestoredTiles_1 = [];
                            var removedIds_1 = [];
                            gluedTiles.forEach(function (gluedTile) {
                                var gluedTileData = gluedTile.data;
                                // Flatten defensively — self-heals any pre-existing nested gluedFrom
                                // data (from before glue was fixed to always flatten) so a single
                                // unglue always fully restores true leaves.
                                var originalTilesData = flattenToLeaves_1(gluedTile);
                                var originalGroupCenter = { x: 0, y: 0 };
                                if (gluedTileData.shapeType === 'rectangle') {
                                    var minX = Math.min.apply(Math, originalTilesData.map(function (t) { return t.center.x; }));
                                    var avgY = originalTilesData.reduce(function (sum, t) { return sum + t.center.y; }, 0) /
                                        originalTilesData.length;
                                    originalGroupCenter = { x: minX, y: avgY };
                                }
                                else {
                                    originalGroupCenter = originalTilesData[0].center;
                                }
                                var deltaX = gluedTileData.center.x - originalGroupCenter.x;
                                var deltaY = gluedTileData.center.y - originalGroupCenter.y;
                                var restoredTiles = originalTilesData.map(function (oldData) {
                                    var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                                    var newCenter = {
                                        x: oldData.center.x + deltaX,
                                        y: oldData.center.y + deltaY,
                                    };
                                    return {
                                        type: 'fractionTiles',
                                        data: __assign(__assign({}, oldData), { id: newId, center: newCenter, lock: { positioning: false, editing: false } }),
                                        widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                                    };
                                });
                                var oldTileData = JSON.parse(JSON.stringify(gluedTile));
                                newStateToSave.push({
                                    type: 'glueFractionTiles',
                                    id: gluedTileData.id.split('_')[1],
                                    propertyName: 'unglue',
                                    old: JSON.stringify({ removedTile: oldTileData, createdTiles: restoredTiles }),
                                });
                                allRestoredTiles_1.push.apply(allRestoredTiles_1, restoredTiles);
                                removedIds_1.push(gluedTileData.id);
                            });
                            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return !removedIds_1.includes(m.data.id); }), true), allRestoredTiles_1, true); });
                            var newIds = allRestoredTiles_1.map(function (t) { return t.data.id; });
                            setSelectedTool(newIds);
                            setSelectedManipulativeId(newIds[0]);
                            setTimeout(function () { return announceAssertive('Tiles separated'); }, 200);
                        }
                    }
                    break;
                }
                case 'splitFractionTile':
                    if ((_l = data.data) === null || _l === void 0 ? void 0 : _l.currentDenominator) {
                        setSplitFractionTileCurrentDenominator(data.data.currentDenominator);
                    }
                    if ((_m = data.data) === null || _m === void 0 ? void 0 : _m.currentNumerator) {
                        setSplitFractionTileCurrentNumerator(data.data.currentNumerator);
                    }
                    setShowSplitFractionTileModal(true);
                    break;
                case 'splitFractionTileDone': {
                    var parts_1 = data.data.parts;
                    if (!parts_1 || parts_1 < 2)
                        break;
                    var selectedId_1 = selectedTool[0];
                    if (!selectedId_1 || !selectedId_1.startsWith('fractionTiles_'))
                        break;
                    // selectedTool may carry a suffix from label focus (e.g. _custom_label_rect_1).
                    // Match via startsWith to find the actual tile.
                    var parentTile = manipulative.find(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            (m.data.id === selectedId_1 || selectedId_1.startsWith(m.data.id + '_'));
                    });
                    if (!parentTile)
                        break;
                    var parentData = parentTile.data;
                    // Calculate current value as a fraction
                    var currentNumerator = (_o = parentData.customNumerator) !== null && _o !== void 0 ? _o : 1;
                    var currentDenominator = (_p = parentData.customDenominator) !== null && _p !== void 0 ? _p : getDenominatorFromFractionType(parentData.fractionSize);
                    // New fraction for each piece: (currentNumerator / currentDenominator) / parts
                    var finalNumerator = currentNumerator;
                    var finalDenominator = currentDenominator * parts_1;
                    // Simplify the fraction
                    var common = gcd(finalNumerator, finalDenominator);
                    finalNumerator /= common;
                    finalDenominator /= common;
                    var newFractionType = finalDenominator === 1
                        ? "fraction-".concat(finalNumerator)
                        : "fraction-".concat(finalNumerator, "-").concat(finalDenominator);
                    var isCircle = parentData.shapeType === 'circle';
                    var newTiles_1 = [];
                    if (isCircle) {
                        var actualSweep = (_t = (_q = parentData.customSweep) !== null && _q !== void 0 ? _q : (_s = (_r = FRACTION_CONFIG[parentData.fractionSize]) === null || _r === void 0 ? void 0 : _r.circle) === null || _s === void 0 ? void 0 : _s.sweep) !== null && _t !== void 0 ? _t : 360;
                        var newSweep = actualSweep / parts_1;
                        // Get the absolute start rotation of the parent
                        var startRotation = (_u = parentData.rotation) !== null && _u !== void 0 ? _u : 0;
                        if (parentData.customSweep == null) {
                            var config = ((_v = FRACTION_CONFIG[parentData.fractionSize]) === null || _v === void 0 ? void 0 : _v.circle) || {
                                start: 0,
                                sweep: 360,
                            };
                            startRotation = (config.start + startRotation) % 360;
                        }
                        var circleSplitSnappedId = uuidv4();
                        for (var i = 0; i < parts_1; i++) {
                            var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            var newTile = {
                                type: 'fractionTiles',
                                data: __assign(__assign({}, JSON.parse(JSON.stringify(parentData))), { id: newId, fractionSize: newFractionType, customSweep: newSweep, rotation: (startRotation + i * newSweep) % 360, customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: undefined, customLabel: undefined, snappedId: circleSplitSnappedId }),
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            newTiles_1.push(newTile);
                        }
                    }
                    else {
                        var parentWidth = parentData.dimensions.width;
                        var parentHeight = parentData.dimensions.height;
                        var parentLeft = (_z = (_x = (_w = parentData.center) === null || _w === void 0 ? void 0 : _w.x) !== null && _x !== void 0 ? _x : (_y = parentData.position) === null || _y === void 0 ? void 0 : _y.x) !== null && _z !== void 0 ? _z : 0;
                        var parentCenterY = (_3 = (_1 = (_0 = parentData.center) === null || _0 === void 0 ? void 0 : _0.y) !== null && _1 !== void 0 ? _1 : (_2 = parentData.position) === null || _2 === void 0 ? void 0 : _2.y) !== null && _3 !== void 0 ? _3 : 0;
                        var parentScale = (_4 = parentData.scale) !== null && _4 !== void 0 ? _4 : 1;
                        // Use exact fractional width so every tile is identical and all parts together
                        // span exactly the parent width. The browser handles subpixel rendering uniformly.
                        var newWidth = parentWidth / parts_1;
                        var rectSplitSnappedId = uuidv4();
                        for (var i = 0; i < parts_1; i++) {
                            var newId = "fractionTiles_".concat(uuidv4().replace(/-/g, ''));
                            var newLeft = parentLeft + i * newWidth;
                            var newTile = {
                                type: 'fractionTiles',
                                data: __assign(__assign({}, JSON.parse(JSON.stringify(parentData))), { id: newId, fractionSize: newFractionType, dimensions: { width: newWidth, height: parentHeight }, defaultDimensions: {
                                        width: newWidth / parentScale,
                                        height: parentHeight / parentScale,
                                    }, center: { x: newLeft, y: parentCenterY }, position: { x: newLeft, y: parentCenterY, z: (_5 = parentData.position) === null || _5 === void 0 ? void 0 : _5.z }, customNumerator: finalNumerator, customDenominator: finalDenominator, gluedFrom: undefined, customLabel: undefined, snappedId: rectSplitSnappedId }),
                                widgetId: "fractionTiles:".concat(uuidv4().replace(/-/g, '')),
                            };
                            newTiles_1.push(newTile);
                        }
                    }
                    var parentId_1 = parentTile.data.id;
                    newStateToSave.push({
                        type: 'splitFractionTile',
                        id: parentId_1.split('_')[1],
                        propertyName: 'split',
                        old: JSON.stringify({ removedTile: parentTile, createdTiles: newTiles_1 }),
                    });
                    setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev.filter(function (m) { return m.data.id !== parentId_1; }), true), newTiles_1, true); });
                    var newIds = newTiles_1.map(function (t) { return t.data.id; }).filter(function (id) { return !!id; });
                    setSelectedManipulativeId(newIds[0]);
                    // Select only the first split tile so the auto-focus guard fires and
                    // keyboard focus lands on it (selectedTool.length must equal 1).
                    setSelectedTool([newIds[0]]);
                    playAudio('scissor');
                    setTimeout(function () { return announceAssertive("Tile split into ".concat(parts_1, " parts")); }, 200);
                    break;
                }
                case 'scaleMinus':
                case 'scalePlus': {
                    // When the label MathEditor is focused, selectedTool contains
                    // "fractionTiles_xxx_custom_label_rect_1" — strip the label suffix
                    // to recover the actual tile ID.
                    var toTileId_1 = function (t) { return t.replace(/_custom_label_(rect|circle)_\d+$/, ''); };
                    // Resolve selectedTool to concrete fraction-tile ids: a manipulativeGroup_
                    // entry (the group is the active selection) expands to its member tile
                    // ids, exactly like NumberAndMoneyPieceManipulative resolves group
                    // selections before acting on them — otherwise scale +/- would silently
                    // no-op whenever a fraction-tile group (rather than a bare tile id) is
                    // the current selection.
                    var resolvedFtIds = selectedTool.flatMap(function (t) {
                        if (t.startsWith('manipulativeGroup_')) {
                            return manipulative
                                .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === t; })
                                .map(function (m) { return m.data.id; });
                        }
                        return t.startsWith('fractionTiles_') ? [toTileId_1(t)] : [];
                    });
                    // Only handle when at least one fraction tile is selected (directly or via a group)
                    var hasFractionTileSelected = resolvedFtIds.length > 0;
                    if (!hasFractionTileSelected)
                        break;
                    // Collect the shape types (rectangle / circle) of the selected fraction tiles.
                    var selectedShapeTypes_1 = new Set(resolvedFtIds
                        .map(function (tileId) {
                        var tile = manipulative.find(function (m) { return m.data.id === tileId; });
                        return tile ? tile.data.shapeType : null;
                    })
                        .filter(function (s) { return s !== null; }));
                    // Scale every fraction tile matching the selected shape type(s), not just
                    // the ones currently selected — tiles of the same shape scale in lockstep.
                    var tilesToScale_1 = manipulative.filter(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            selectedShapeTypes_1.has(m.data.shapeType);
                    });
                    if (tilesToScale_1.length === 0)
                        break;
                    // Announce the new scale to screen readers using the first tile's value.
                    // All selected tiles scale together, so one announcement is sufficient.
                    {
                        var firstTileData = tilesToScale_1[0].data;
                        var oldScale = (_6 = firstTileData.scale) !== null && _6 !== void 0 ? _6 : 1;
                        var newScale_1 = data.id === 'scaleMinus'
                            ? Math.max(0.5, Math.round((oldScale - 0.1) * 10) / 10)
                            : Math.round((oldScale + 0.1) * 10) / 10;
                        setTimeout(function () { return announceAssertive("Scale updated to ".concat(newScale_1.toFixed(1))); }, 200);
                    }
                    // Pre-compute new scale, dimensions, and base info for each tile
                    var tileScaleInfo_1 = new Map();
                    tilesToScale_1.forEach(function (m) {
                        var _a, _b;
                        var tileData = m.data;
                        var oldScale = (_a = tileData.scale) !== null && _a !== void 0 ? _a : 1;
                        var newScale = data.id === 'scaleMinus' ? Math.max(0.5, oldScale - 0.1) : oldScale + 0.1;
                        var isGlued = tileData.gluedFrom && tileData.gluedFrom.length > 0;
                        var baseDim = (_b = tileData.defaultDimensions) !== null && _b !== void 0 ? _b : (function () {
                            var _a, _b;
                            var config = getFractionDimensions(tileData.fractionSize);
                            return { width: (_b = (_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 660, height: 55 };
                        })();
                        var baseWidth = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.width;
                        var baseHeight = tileData.shapeType === 'circle' && !isGlued ? 300 : baseDim.height;
                        tileScaleInfo_1.set(tileData.id, {
                            newScale: newScale,
                            newWidth: baseWidth * newScale,
                            newHeight: baseHeight * newScale,
                        });
                        newStateToSave.push({
                            type: tileData.id,
                            id: tileData.id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(oldScale),
                            new: JSON.stringify(newScale),
                        });
                    });
                    // For rectangles: reposition adjacent tiles so they stay snapped after scaling.
                    // Detect chains of adjacent tiles (by y-alignment + right-edge-to-left-edge)
                    // and recompute x positions using new widths.
                    var ADJ_TOL_1 = 5; // horizontal adjacency tolerance (px)
                    var newPositions_1 = {};
                    var rectTiles = tilesToScale_1
                        .map(function (m) { return m.data; })
                        .filter(function (d) { return d.shapeType === 'rectangle'; });
                    // Group tiles into rows purely by y-alignment. `snappedId` is not used here —
                    // it's a separately-maintained field that can go stale relative to a tile's
                    // actual position (asymmetric assignment on creation, cleared on drag-start,
                    // regenerated on split), so two tiles that are genuinely touching right now can
                    // carry mismatched snappedIds. Grouping by y alone is safe because the actual
                    // adjacency chain below only links tiles whose edges truly touch (within ADJ_TOL).
                    var assigned_1 = new Set();
                    var rows = [];
                    var _loop_1 = function (tile) {
                        if (assigned_1.has(tile.id))
                            return "continue";
                        var row = rectTiles.filter(function (t) { return !assigned_1.has(t.id) && Math.abs(t.center.y - tile.center.y) < ADJ_TOL_1; });
                        row.sort(function (a, b) { return a.center.x - b.center.x; });
                        row.forEach(function (t) { return assigned_1.add(t.id); });
                        rows.push(row);
                    };
                    for (var _14 = 0, rectTiles_1 = rectTiles; _14 < rectTiles_1.length; _14++) {
                        var tile = rectTiles_1[_14];
                        _loop_1(tile);
                    }
                    // Tiles whose X is owned by the horizontal chain logic below — right-edge
                    // anchoring (further down) must not override these.
                    var horizPositionedIds = new Set();
                    if (rectTiles.length > 1) {
                        // Within each row, find chains of adjacent tiles and reposition horizontally.
                        // Tiles touch edge-to-edge (BORDER_OVERLAP=0), so adjacency = gap within ADJ_TOL.
                        for (var _15 = 0, rows_1 = rows; _15 < rows_1.length; _15++) {
                            var row = rows_1[_15];
                            var chainStart = 0;
                            for (var i = 0; i < row.length; i++) {
                                var rightEdge = row[i].center.x + row[i].dimensions.width;
                                var isAdjacentToNext = i < row.length - 1 &&
                                    rightEdge - row[i + 1].center.x >= -ADJ_TOL_1 &&
                                    rightEdge - row[i + 1].center.x <= ADJ_TOL_1;
                                if (!isAdjacentToNext || i === row.length - 1) {
                                    // Process chain [chainStart..i] — first tile keeps its position.
                                    if (i > chainStart) {
                                        var currentX = row[chainStart].center.x;
                                        for (var j = chainStart; j <= i; j++) {
                                            var info = tileScaleInfo_1.get(row[j].id);
                                            if (j > chainStart && info) {
                                                newPositions_1[row[j].id] = { x: currentX, y: row[j].center.y };
                                                horizPositionedIds.add(row[j].id);
                                            }
                                            currentX += (_7 = info === null || info === void 0 ? void 0 : info.newWidth) !== null && _7 !== void 0 ? _7 : row[j].dimensions.width;
                                        }
                                    }
                                    chainStart = i + 1;
                                }
                            }
                        }
                    }
                    // For rectangles: reposition vertically adjacent rows so they stay snapped after scaling.
                    // Rows are chained purely by minX alignment (see below) rather than snappedId — that
                    // tolerance check is precise enough on its own to avoid bleeding unrelated rows together.
                    if (rectTiles.length > 0) {
                        // Sort all rows top-to-bottom
                        var sortedRows = __spreadArray([], rows, true).sort(function (a, b) { return a[0].center.y - b[0].center.y; });
                        // Pre-compute horizontal extents and new heights for each row.
                        var rowExtents = sortedRows.map(function (row) { return ({
                            minX: Math.min.apply(Math, row.map(function (t) { return t.center.x; })),
                            maxX: Math.max.apply(Math, row.map(function (t) { return t.center.x + t.dimensions.width; })),
                        }); });
                        var rowNewYArr = sortedRows.map(function (row) { return row[0].center.y; });
                        var rowNewHeightArr = sortedRows.map(function (row) {
                            var _a;
                            var info = tileScaleInfo_1.get(row[0].id);
                            return (_a = info === null || info === void 0 ? void 0 : info.newHeight) !== null && _a !== void 0 ? _a : row[0].dimensions.height;
                        });
                        var _loop_2 = function (ri) {
                            var row = sortedRows[ri];
                            var rowY = row[0].center.y;
                            var curExtent = rowExtents[ri];
                            var parentRi = -1;
                            for (var pj = ri - 1; pj >= 0; pj--) {
                                var pExt = rowExtents[pj];
                                // Rows are in the same vertical column if their leftmost tiles start at
                                // approximately the same X. Using minX (not full extent) is scale-invariant
                                // because the leftmost tile is the horizontal anchor and never gets repositioned.
                                if (Math.abs(pExt.minX - curExtent.minX) < ADJ_TOL_1) {
                                    parentRi = pj;
                                    break;
                                }
                            }
                            var newY = parentRi >= 0
                                ? rowNewYArr[parentRi] + rowNewHeightArr[parentRi]
                                : rowY; // no overlapping parent — keep original Y (different column)
                            rowNewYArr[ri] = newY;
                            if (Math.abs(newY - rowY) > 0.1) {
                                row.forEach(function (tile) {
                                    var _a;
                                    var existingPos = newPositions_1[tile.id];
                                    newPositions_1[tile.id] = {
                                        x: (_a = existingPos === null || existingPos === void 0 ? void 0 : existingPos.x) !== null && _a !== void 0 ? _a : tile.center.x,
                                        y: newY,
                                    };
                                });
                            }
                        };
                        // For each row (top-to-bottom), find the nearest row above it that
                        // horizontally overlaps — that is its vertical parent for chaining.
                        // Rows in different columns (side-by-side, no horizontal overlap) do
                        // NOT chain, so they each remain anchored at their own Y.
                        for (var ri = 1; ri < sortedRows.length; ri++) {
                            _loop_2(ri);
                        }
                    }
                    // Right-edge anchoring: a tile whose original right edge matches another tile's
                    // original right edge (with that tile positioned above it) tracks that tile's width
                    // growth, shifting its X so its own right edge stays flush as the referenced tile
                    // widens. This covers snap layouts where a smaller tile is right-aligned under a
                    // wider tile rather than left-aligned — the row/chain logic above only tracks left
                    // edges, so it never repositions this case. Skips tiles already owned by the
                    // horizontal chain, since chained siblings' X is determined by their row-neighbor.
                    //
                    // Processed top-to-bottom (not creation order) so that when an anchor is itself
                    // a dependent — either a non-first member of a horizontal chain (its X already
                    // shifted above) or a lower anchor in a multi-level right-aligned stack (its X
                    // already computed by an earlier iteration of this very loop) — that shift is
                    // available via `newPositions[anchor.id]` before it's read as a dependency here.
                    // Without folding the anchor's own X-shift in (not just its width growth), a
                    // chained or stacked anchor's dependents silently drift out of right-alignment.
                    var rectTilesTopToBottom = __spreadArray([], rectTiles, true).sort(function (a, b) { return a.center.y - b.center.y; });
                    for (var _16 = 0, rectTilesTopToBottom_1 = rectTilesTopToBottom; _16 < rectTilesTopToBottom_1.length; _16++) {
                        var tile = rectTilesTopToBottom_1[_16];
                        if (horizPositionedIds.has(tile.id))
                            continue;
                        var tileRightEdge = tile.center.x + tile.dimensions.width;
                        var anchor = void 0;
                        for (var _17 = 0, rectTilesTopToBottom_2 = rectTilesTopToBottom; _17 < rectTilesTopToBottom_2.length; _17++) {
                            var candidate = rectTilesTopToBottom_2[_17];
                            if (candidate.id === tile.id)
                                continue;
                            if (candidate.center.y >= tile.center.y - ADJ_TOL_1)
                                continue; // must be above
                            var candidateRightEdge = candidate.center.x + candidate.dimensions.width;
                            if (Math.abs(candidateRightEdge - tileRightEdge) >= ADJ_TOL_1)
                                continue;
                            // Nearest candidate above wins, matching the left-edge parent search convention.
                            if (!anchor || candidate.center.y > anchor.center.y)
                                anchor = candidate;
                        }
                        if (!anchor)
                            continue;
                        var anchorInfo = tileScaleInfo_1.get(anchor.id);
                        if (!anchorInfo)
                            continue;
                        // Net delta: the tile's own width growth already pushes its right edge
                        // rightward on its own (it grows from a fixed left anchor), so only the
                        // anchor's growth beyond the tile's own growth needs to be applied as an X shift.
                        // The anchor's own X-shift (if it's itself chained or stacked) is added on top,
                        // since its right edge moves by that shift as well as by its width growth.
                        var tileInfo = tileScaleInfo_1.get(tile.id);
                        var ownWidthDelta = ((_8 = tileInfo === null || tileInfo === void 0 ? void 0 : tileInfo.newWidth) !== null && _8 !== void 0 ? _8 : tile.dimensions.width) - tile.dimensions.width;
                        var anchorXShift = ((_10 = (_9 = newPositions_1[anchor.id]) === null || _9 === void 0 ? void 0 : _9.x) !== null && _10 !== void 0 ? _10 : anchor.center.x) - anchor.center.x;
                        var anchorWidthGrowth = anchorInfo.newWidth - anchor.dimensions.width;
                        var widthDelta = anchorXShift + anchorWidthGrowth - ownWidthDelta;
                        if (Math.abs(widthDelta) < 0.1)
                            continue;
                        var existingPos = newPositions_1[tile.id];
                        newPositions_1[tile.id] = {
                            x: tile.center.x + widthDelta,
                            y: (_11 = existingPos === null || existingPos === void 0 ? void 0 : existingPos.y) !== null && _11 !== void 0 ? _11 : tile.center.y,
                        };
                    }
                    // Save undo entries for any position changes
                    Object.entries(newPositions_1).forEach(function (_a) {
                        var tileId = _a[0], newCenter = _a[1];
                        var original = tilesToScale_1.find(function (m) { return m.data.id === tileId; });
                        if (original) {
                            newStateToSave.push({
                                type: tileId,
                                id: tileId.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(original.data.center),
                                new: JSON.stringify(newCenter),
                            });
                        }
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.type !== 'fractionTiles')
                                return item;
                            var tileData = item.data;
                            if (!selectedShapeTypes_1.has(tileData.shapeType))
                                return item;
                            var info = tileScaleInfo_1.get(tileData.id);
                            if (!info)
                                return item;
                            var newCenter = newPositions_1[tileData.id]
                                ? newPositions_1[tileData.id]
                                : tileData.center;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: info.newScale, dimensions: { width: info.newWidth, height: info.newHeight }, center: newCenter }) });
                        });
                    });
                    break;
                }
            }
            // Save all state changes as a batch if any
            if (newStateToSave.length > 0) {
                saveState(newStateToSave);
                customEventHandler.dispatchEvent('stateChanged', { type: 'save' });
            }
        };
        customEventHandler.addEventListener('bottomPanel', handleEvents);
        customEventHandler.addEventListener('sidebarClicked', handleEvents);
        customEventHandler.addEventListener('headerClicked', handleEvents);
        customEventHandler.addEventListener('bottomBar', handleEvents);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', handleEvents);
            customEventHandler.removeEventListener('sidebarClicked', handleEvents);
            customEventHandler.removeEventListener('headerClicked', handleEvents);
            customEventHandler.removeEventListener('bottomBar', handleEvents);
        };
    }, [
        manipulative,
        selectedTool,
        selectedManipulativeId,
        zoomLevel,
        playerBgPattern,
        setShowSplitFractionTileModal,
        setSplitFractionTileCurrentDenominator,
        setManipulative,
        setSelectedTool,
        setSelectedManipulativeId,
    ]);
    return null;
};
export default FractionTileController;
